/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Transcode Requests API Client
 *
 * Handles all transcode request operations including:
 * - EDL upload and parsing
 * - CRUD operations for transcode requests
 * - Completion tracking
 * - VFX shot linking
 *
 * Related: AIO-40, AIO-41, AIO-42
 */

import type {
  TranscodeRequest,
  TranscodeRequestWithShots,
  TranscodeUploadResult,
} from '@/types/database'
import { supabase } from '../supabase'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get auth token from Supabase
 */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  return session.access_token
}

/**
 * Make an authenticated request to the backend
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage: string

    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorJson.message || 'Request failed'
    } catch {
      errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`
    }

    throw new Error(errorMessage)
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// =============================================
// TRANSCODE REQUEST OPERATIONS
// =============================================

export interface ListTranscodesResponse {
  transcodes: TranscodeRequest[]
  total: number
  project_id: string
}

/**
 * Upload EDL file and create transcode request
 */
export async function uploadEDL(params: {
  projectId: string
  file: File
  vendor?: string
  notes?: string
  fps?: number
}): Promise<TranscodeUploadResult> {
  const { projectId, file, vendor, notes, fps = 24 } = params

  const formData = new FormData()
  formData.append('edl_file', file)
  if (vendor) formData.append('vendor', vendor)
  if (notes) formData.append('notes', notes)
  formData.append('fps', fps.toString())

  const token = await getAuthToken()

  const response = await fetch(
    `${BACKEND_URL}/api/projects/${projectId}/transcodes/upload`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage: string

    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorJson.message || 'Upload failed'
    } catch {
      errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`
    }

    throw new Error(errorMessage)
  }

  return response.json()
}

/**
 * List all transcode requests for a project
 */
export async function listTranscodes(
  projectId: string,
  options: {
    limit?: number
    offset?: number
  } = {}
): Promise<ListTranscodesResponse> {
  const params = new URLSearchParams()
  if (options.limit) params.append('limit', options.limit.toString())
  if (options.offset) params.append('offset', options.offset.toString())

  const queryString = params.toString()
  const endpoint = `/api/projects/${projectId}/transcodes${
    queryString ? `?${queryString}` : ''
  }`

  return apiRequest<ListTranscodesResponse>(endpoint)
}

/**
 * Get a transcode request with linked VFX shots
 */
export async function getTranscode(
  transcodeId: string
): Promise<TranscodeRequestWithShots> {
  return apiRequest<TranscodeRequestWithShots>(`/api/transcodes/${transcodeId}`)
}

/**
 * Mark transcode as complete
 */
export async function markTranscodeComplete(params: {
  transcodeId: string
  notes?: string
}): Promise<TranscodeRequest> {
  const { transcodeId, notes } = params

  return apiRequest<TranscodeRequest>(
    `/api/transcodes/${transcodeId}/complete`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notes }),
    }
  )
}

/**
 * Delete a transcode request
 */
export async function deleteTranscode(transcodeId: string): Promise<void> {
  return apiRequest<void>(`/api/transcodes/${transcodeId}`, {
    method: 'DELETE',
  })
}
